// activity.js

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';
import { Button, Spinner, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import PaginatedListTable from '../../components/PaginatedListTable';
import ViewActivityModal from '../../components/ViewActivityModal';
import Container from '../../layouts/Container';
import { getActivities } from '../../services/project/projectService';
import { formatLocalDateTime, getToday } from '../../utils/dateUtil'; // ⬅️ getToday added
import { formatScope } from '../../utils/roleUtil';
import { getAreas } from '../../services/project/areaService';
import { getRegions } from '../../services/project/projectService';
import { downloadActivities } from '../../services/project/reportService';

function Activity() {
  const tableRef = useRef();
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const { projectId } = useParams();

  // ⬇️ Date range state (same default behavior as Reports)
  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getToday());

  const [isDownloading, setIsDownloading] = useState(false);

  // Build ISO strings in +08:00 just like Reports
  const startDateUTC = useMemo(
    () => new Date(`${startDate}T00:00:00+08:00`).toISOString(),
    [startDate]
  );
  const endDateUTC = useMemo(
    () => new Date(`${endDate}T23:59:59+08:00`).toISOString(),
    [endDate]
  );

  const handleApply = () => {
    // reset to first page & refetch using current date range
    tableRef.current?.refetch({ page: 0 });
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await downloadActivities(
        {
          projectId,
          startDate: startDateUTC,
          endDate: endDateUTC,
        },
        `activities_${projectId}_${startDate}_to_${endDate}.csv`
      );
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const fetchRegionsAndAreas = async () => {
      const regionResponse = await getRegions(projectId);
      if (!regionResponse.success) {
        toast.error(regionResponse.data?.errorMessage || 'Error fetching regions.');
        return;
      }
      const fetchedRegions = regionResponse.regions || [];
      setRegions(fetchedRegions);

      const areaResponses = await Promise.all(
        fetchedRegions.map(r => getAreas(projectId, r.id))
      );
      const allAreas = areaResponses
        .filter(resp => resp.success)
        .flatMap(resp => resp.areas || []);
      setAreas(allAreas);
    };
    if (projectId) fetchRegionsAndAreas();
  }, [projectId]);

  // 🔁 Adapter for DataTable: must return { data, pageCount }
  const fetchPaginatedActivities = async ({ pageIndex, pageSize, search }) => {
    try {
      const res = await getActivities(projectId, {
        page: pageIndex,
        size: pageSize,
        search,
        // ⬇️ pass the same date range used by Export
        startDate: startDateUTC,
        endDate: endDateUTC,
      });

      const rows = (res.data || []).map(a => ({
        id: a.id,
        username: a.user?.username || '-',
        roles: a.user?.roles || [],
        raw: a,
      }));

      return { data: rows, pageCount: res.pageCount || res.totalPages || 0, totalItems: res.totalItems || 0 };
    } catch (e) {
      console.error(e);
      toast.error('Error fetching activities.');
      return { data: [], pageCount: 0, totalItems: 0 };
    }
  };

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor('username', { header: 'Username' }),
    columnHelper.display({
      id: 'checkin',
      header: 'Check-in',
      cell: info => {
        const a = info.row.original.raw;
        return a?.checkInAt ? formatLocalDateTime(a.checkInAt) : '—';
      },
    }),
    columnHelper.display({
      id: 'checkout',
      header: 'Check-out',
      cell: info => {
        const a = info.row.original.raw;
        return a?.checkOutAt ? formatLocalDateTime(a.checkOutAt) : '—';
      },
    }),
    columnHelper.display({
      id: 'roles',
      header: 'Role',
      cell: info => {
        const roles = info.row.original?.roles;
        const formatted = formatScope(roles, regions, areas);
        return (
          <div className="d-flex flex-column gap-1">
            {formatted.map((entry, idx) => (
              <div key={idx}>{entry.roleName}</div>
            ))}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'scope',
      header: 'Scope',
      cell: info => {
        const roles = info.row.original?.roles;
        const formatted = formatScope(roles, regions, areas);
        return (
          <div className="d-flex flex-column gap-1">
            {formatted.map((entry, idx) => (
              <div key={idx}>
                {entry.scopeDetails.map((sc, i) => (
                  <div key={i}>
                    <strong>{sc.regionName}</strong> - {sc.areaNames}
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      },
    }),
  ];

  const handleRowClick = row => {
    setSelectedActivity(row.raw);
    setShowModal(true);
  };

  return (
    <Container>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h2 className="mb-0">Activity List</h2>

        {/* ⬇️ Date-range controls, mirroring Reports */}
        <Form className="d-flex align-items-center gap-3 flex-wrap mb-0">
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">Start</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">End</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Form.Group>

          <Button variant="primary" type="button" onClick={handleApply}>
            Apply
          </Button>

          <Button variant="success" disabled={isDownloading} onClick={handleDownload}>
            {isDownloading ? <Spinner size="sm" /> : 'Export'}
          </Button>
        </Form>
      </div>

      <PaginatedListTable
        ref={tableRef}
        tableId="activity-list"
        columns={columns}
        onFetchData={fetchPaginatedActivities}
        onRowClick={handleRowClick}
      />

      <ViewActivityModal
        show={showModal}
        activity={selectedActivity}
        onClose={() => setShowModal(false)}
      />
    </Container>
  );
}

export default Activity;
